// ---------------------------------------------------------
// Datei: ProjectsScreen.tsx
// Zweck: Hauptseite für die Anzeige und Verwaltung von Projekten
// Besonderheiten:
// - Verwendet MyProjectsGrid für Anzeige der Projekte mit Heute-Filter
// - Modal zum Erstellen neuer Projekte
// - Header mit Benutzerinfo und Logout
// - SyncStatus-Anzeige am unteren Rand
// - ThemeToggle für Light/Dark Mode (Sprint 2 Task 1)
// - Overflow-Menu für sekundäre Actions (Logout)
// - Suchleiste zum Filtern der Deals (NEU)
// ---------------------------------------------------------

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { resolveOwnersByEmailAsync as resolveHubSpotOwnerIdsByEmail } from "../services/api/hubspot";
import { useDealsStore } from "../store/dealsStore";
import SyncStatus from "../components/SyncStatus";
import ViewToggle from "../components/ViewToggle";
import AppHeader from "../components/AppHeader";
import ProjectScopeTabs from "../components/ProjectScopeTabs";
import ProjectMilestoneTimeline from "../components/ProjectMilestoneTimeline";
import DealStatusFilterBar, { DealStageFilter, InterneFreigabeFilter, NacharbeitenFilter } from "../components/DealStatusFilterBar";
import { useToast } from "../contexts/ToastContext";
import {
  getDefaultProjectScope,
  matchesProjectScope,
  persistProjectScope,
  PersistedProjectScope,
} from "../utils/projectScope";

import type { ProjectFormData } from '../interfaces/projects';

function getInterneFreigabeStatus(deal: any): "draft" | "in_review" | "ready" | "approved" | null {
  const direct = deal?.project_status?.projektdokumentationApprovalStatus;
  if (direct === "draft" || direct === "in_review" || direct === "ready" || direct === "approved") {
    return direct;
  }

  const projektdokumentationPhase = (deal?.project_status?.phases || []).find(
    (phase: any) => phase?.phaseKey === "projektdokumentation"
  );

  if (!projektdokumentationPhase) {
    return null;
  }

  // Legacy fallback: ältere Snapshots hatten das Approval-Statusfeld noch nicht.
  // Wenn approvedByEmail fehlt, ist "completed" sehr wahrscheinlich "ready".
  if (projektdokumentationPhase.status === "completed") {
    return projektdokumentationPhase.approvedByEmail ? "approved" : "ready";
  }

  if (projektdokumentationPhase.status === "open") {
    return "draft";
  }

  // Legacy-Heuristik: ältere Snapshots ohne explizites Approval-Statusfeld.
  if (projektdokumentationPhase.status === "in_progress") {
    const completion = Number(projektdokumentationPhase.completionPercent || 0);
    const missingRequired = Number(projektdokumentationPhase.missingRequiredCount || 0);
    if (completion >= 100 && missingRequired === 0) {
      return "ready";
    }
    if (completion > 0) {
      return "in_review";
    }
  }

  return null;
}

const ProjectsScreen: React. FC = () => {
  // === State-Definitionen ===
  const [localError, setLocalError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [scope, setScope] = useState<PersistedProjectScope>(() => getDefaultProjectScope());
  const [dealStageFilter, setDealStageFilter] = useState<DealStageFilter>('all');
  const [nacharbeitenFilter, setNacharbeitenFilter] = useState<NacharbeitenFilter>('all');
  const [interneFreigabeFilter, setInterneFreigabeFilter] = useState<InterneFreigabeFilter>('all');
  // NEU: State für Suchleiste
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hubspotOwnerIds, setHubspotOwnerIds] = useState<string[]>([]);
  const skipNextFilterFetchRef = useRef(false);

  // Form-States für neues Projekt
  const [newProject, setNewProject] = useState<ProjectFormData>({
    name: "",
    kunde: "",
    adresse: "",
    anlagentyp: "Luft/Wasser",
    projektStatus:  "Geplant",
  });

  // Auth-Context für Benutzerinfo und Logout
  const { user, logout } = useAuth();
  const { showToast } = useToast();

  // Navigation-Hook für Projekt-Klick
  const navigate = useNavigate();

  // Deals aus dem persistierten Store (offline-fähig)
  const {
    deals,
    searchResults,
    isLoading: dealsLoading,
    isLoadingMore,
    hasMore,
    error: dealsError,
    loadMoreError,
    isSearching,
    searchError,
    fetchDeals,
    searchDealsIntoCache,
    clearLoadMoreError,
  } = useDealsStore();

  const stageMap: Record<Exclude<DealStageFilter, 'all'>, string> = {
    'kunde-wartet': 'Kunde wartet',
    'projekt-angenommen': 'Projekt Angenommen',
    'projekt-abgeschlossen': 'Projekt Abgeschlossen',
    'durchfuehrung-abgeschlossen': 'Durchführung abgeschlossen',
    waiting: 'waiting',
    dubletten: 'Dubletten',
    'anfrage-zum-kennenlernen-verschicken': 'Anfrage zum Kennenlernen verschicken',
  };

  // Gefilterte Deals basierend auf Suchbegriff
  const filteredDeals = useMemo(() => {
    const sourceDeals = searchQuery.trim().length > 0 ? (searchResults || []) : deals;
    if (searchQuery.trim().length > 0) {
      return sourceDeals;
    }

    return sourceDeals
      .filter((deal) => matchesProjectScope(deal, scope, user, { hubspotOwnerIds }))
      .filter((deal) => {
        if (nacharbeitenFilter === 'all') return true;
        const hasOpenNacharbeiten = Boolean(deal.project_status?.nacharbeiten?.hasOpenNacharbeiten);
        return nacharbeitenFilter === 'yes' ? hasOpenNacharbeiten : !hasOpenNacharbeiten;
      })
      .filter((deal) => {
        if (interneFreigabeFilter === 'all') return true;
        const status = getInterneFreigabeStatus(deal);
        if (interneFreigabeFilter === 'empty_or_draft') {
          return status === null || status === 'draft';
        }
        return status === interneFreigabeFilter;
      });
  }, [deals, hubspotOwnerIds, interneFreigabeFilter, nacharbeitenFilter, scope, searchQuery, searchResults, user]);

  // === Navigation-Handler ===
  const handleDealClick = (dealId: string) => {
    navigate(`/projects/${dealId}`);
  };

  // === Event-Handler ===
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const email = user?.email?.trim();
    if (!email) {
      setHubspotOwnerIds([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (!navigator.onLine) return;
      const ownerIds = await resolveHubSpotOwnerIdsByEmail(email);
      if (cancelled) return;
      setHubspotOwnerIds(ownerIds);
    };

    void load();

    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener('online', load);
    };
  }, [user?.email]);

  useEffect(() => {
    if (!loadMoreError) return;
    showToast('warning', 'Weitere Projekte konnten nicht nachgeladen werden. Die vorhandenen Projekte bleiben nutzbar.', 5000);
    clearLoadMoreError();
  }, [clearLoadMoreError, loadMoreError, showToast]);

  useEffect(() => {
    if (!searchError) return;
    showToast('warning', searchError, 5000);
  }, [searchError, showToast]);

  useEffect(() => {
    const handleRefresh = () => {
      void fetchDeals({
        forceRefresh: true,
        cacheOwnerKey: user?.email || user?.id || null,
        stageLabel: dealStageFilter === 'all' ? undefined : stageMap[dealStageFilter],
      });
    };

    window.addEventListener('project-status-refresh', handleRefresh);
    return () => window.removeEventListener('project-status-refresh', handleRefresh);
  }, [dealStageFilter, fetchDeals, user?.email, user?.id]);

  useEffect(() => {
    if (searchQuery.trim().length > 0) return;
    if (skipNextFilterFetchRef.current) {
      skipNextFilterFetchRef.current = false;
      return;
    }
    const hasClientOnlyFilters =
      nacharbeitenFilter !== 'all' ||
      interneFreigabeFilter !== 'all';
    if (dealStageFilter === 'all' && hasClientOnlyFilters) {
      if (deals.length === 0) {
        void fetchDeals({
          cacheOwnerKey: user?.email || user?.id || null,
          restoreUnfilteredFromCache: true,
        });
      }
      return;
    }
    const restoreUnfilteredFromCache =
      dealStageFilter === 'all' &&
      nacharbeitenFilter === 'all' &&
      interneFreigabeFilter === 'all';
    void fetchDeals({
      cacheOwnerKey: user?.email || user?.id || null,
      stageLabel: dealStageFilter === 'all' ? undefined : stageMap[dealStageFilter],
      restoreUnfilteredFromCache,
    });
  }, [dealStageFilter, deals.length, fetchDeals, interneFreigabeFilter, nacharbeitenFilter, searchQuery, user?.email, user?.id]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void searchDealsIntoCache(q, {
        cacheOwnerKey: user?.email || user?.id || null,
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchDealsIntoCache, searchQuery, user?.email, user?.id]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleScopeChange = (value: PersistedProjectScope) => {
    const hadSearch = searchQuery.trim().length > 0;
    setScope(value);
    persistProjectScope(value);
    setSearchQuery('');
    if (hadSearch) {
      setDealStageFilter('all');
      setNacharbeitenFilter('all');
      setInterneFreigabeFilter('all');
    }
  };

  // Neues Projekt erstellen
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      throw new Error("Projekte werden direkt in HubSpot erstellt.");
    } catch (err: any) {
      setLocalError(err.message || "Fehler beim Erstellen des Projekts");
      console.error("Fehler beim Erstellen des Projekts:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader
        title="360Volt Projekte"
        actions={<ViewToggle />}
        menuContent={(
          <>
            <div className="sm:hidden border-b border-slate-200 px-2 py-2">
              <ViewToggle />
            </div>
            <button
              onClick={logout}
              className="mt-1 flex min-h-[48px] w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
      />
      <ProjectScopeTabs
        value={searchQuery.trim().length > 0 ? 'search' : scope}
        onChange={(value) => {
          if (value === 'search') return;
          handleScopeChange(value);
        }}
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={async () => {
          const q = searchQuery.trim();
          if (q.length >= 3) {
            await searchDealsIntoCache(q);
          }
        }}
        onClearSearch={() => {
          setSearchQuery('');
        }}
        searchLoading={isSearching}
      />

      {/* Hauptinhalt */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 pb-[calc(env(safe-area-inset-bottom)+80px)]">
        <div className="mb-6">
          <DealStatusFilterBar
            dealStageFilter={dealStageFilter}
            nacharbeitenFilter={nacharbeitenFilter}
            interneFreigabeFilter={interneFreigabeFilter}
            onDealStageFilterChange={setDealStageFilter}
            onNacharbeitenFilterChange={setNacharbeitenFilter}
            onInterneFreigabeFilterChange={setInterneFreigabeFilter}
            onReset={() => {
              skipNextFilterFetchRef.current = true;
              setDealStageFilter('all');
              setNacharbeitenFilter('all');
              setInterneFreigabeFilter('all');
              void fetchDeals({
                cacheOwnerKey: user?.email || user?.id || null,
                restoreUnfilteredFromCache: true,
              });
            }}
            onRefresh={() => {
              void fetchDeals({
                forceRefresh: true,
                cacheOwnerKey: user?.email || user?.id || null,
              });
            }}
            disabled={searchQuery.trim().length > 0}
          />
        </div>
        {/* Fehleranzeige */}
        {(localError || dealsError) && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-6">
            {localError || dealsError}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Projekte
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary px-5 py-2 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Neues Projekt
          </button>
        </div>

        <section className="mb-8" data-testid="project-list">
          <div className="mb-4">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{filteredDeals.length} Projekte</p>
            {(isLoadingMore || hasMore) && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Lade weitere Projekte nach...</p>
            )}
            {searchQuery.trim().length >= 3 && isSearching && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Weitere Suchergebnisse werden vom Server geladen...</p>
            )}
          </div>
          {dealsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : filteredDeals.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery 
                  ? `Keine Projekte für "${searchQuery}" gefunden. `
                  : 'Keine Deals gefunden.'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Suche zurücksetzen
                </button>
              )}
            </div>
          ) : (
            <div className="grid min-w-0 gap-6 pb-6 md:grid-cols-2 lg:grid-cols-3" data-testid="project-grid">
              {filteredDeals.map((deal) => (
                <button
                  key={deal.id}
                  data-testid="project-item"
                  data-deal-id={deal.id}
                  onClick={() => handleDealClick(deal.id)}
                  className="w-full min-w-0 cursor-pointer overflow-hidden rounded-2xl border border-black/10 bg-white/95 p-4 text-left shadow-md transition-all hover:border-[var(--color-primary)] hover:shadow-lg active:scale-95 dark:border-white/10 dark:bg-gray-800/95 sm:p-6"
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex min-w-0 items-start justify-between gap-3">
                        <h3 className="min-w-0 break-words text-lg font-semibold leading-snug text-primary-700 line-clamp-2 dark:text-primary-300 sm:truncate">
                          {deal.dealname || (
                            <span className="italic text-gray-400">
                              Kein Name
                            </span>
                          )}
                        </h3>
                        {deal.project_status?.nacharbeiten?.hasOpenNacharbeiten && (
                          <span className="shrink-0 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                            {deal.project_status.nacharbeiten.openNacharbeitenCount} Nacharbeit{deal.project_status.nacharbeiten.openNacharbeitenCount === 1 ? '' : 'en'}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {deal.id}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Deal Status: {deal.dealstage_label || deal.dealstage || "Unbekannt"}
                      </p>
                    </div>
                  </div>

                  {deal.project_status && (
                    <div className="mb-4 space-y-2 min-w-0">
                      <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm dark:bg-slate-900/70">
                        <span className="text-slate-600 dark:text-slate-300">Projektfortschritt</span>
                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                          {deal.project_status.overallCompletionPercent}%
                        </span>
                      </div>
                      <div className="min-w-0 overflow-x-auto sm:overflow-visible">
                        <ProjectMilestoneTimeline
                          phases={deal.project_status.phases}
                          projektdokumentationApprovalStatus={deal.project_status.projektdokumentationApprovalStatus || null}
                          compact
                        />
                      </div>
                    </div>
                  )}

                  {/* Call-to-Action */}
                  <div className="mt-4 flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium">
                    <span>Projekt öffnen</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* "Neues Projekt" Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Neues Projekt
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  value={newProject.name}
                  onChange={handleInputChange}
                  placeholder="Projektname"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  name="kunde"
                  value={newProject.kunde}
                  onChange={handleInputChange}
                  placeholder="Kunde"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  name="adresse"
                  value={newProject.adresse}
                  onChange={handleInputChange}
                  placeholder="Adresse"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-200"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {loading ? "Speichert..." : "Projekt anlegen"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Sync-Status-Anzeige */}
      <SyncStatus />
    </div>
  );
};

export default ProjectsScreen;
